import { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type {
  BugAttachmentRef,
  BugDraft,
  ChatEvidenceItem,
  TestReview,
  RelatedRequirement,
} from '@sr-test-wizard/shared';
import { api } from '../../lib/api';
import { runAssetsApi, type RunImage } from '../../lib/runAssets';
import { runVideosApi } from '../../lib/runVideosApi';

interface JiraProject {
  id: string;
  key: string;
  name: string;
}

interface VideoFrameOption {
  videoSlug: string;
  videoFile: string;
  durationSeconds: number | null;
  frameCount: number;
}

interface StagedUpload {
  id: string;
  filename: string;
  mime: string;
  size: number;
}

const ATTACHMENT_TABS = [
  { id: 'game-rules', label: 'Game rules' },
  { id: 'video-frames', label: 'Video frames' },
  { id: 'chat-evidence', label: 'Chat evidence' },
  { id: 'upload', label: 'Upload' },
] as const;
type AttachmentTab = (typeof ATTACHMENT_TABS)[number]['id'];

const SEVERITIES: Array<'minor' | 'major' | 'blocker'> = ['minor', 'major', 'blocker'];

interface Props {
  runId: string;
  test: TestReview;
  gameName: string;
  /** Pre-filled draft (from "Draft from chat"); undefined = blank. */
  initialDraft?: BugDraft;
  /**
   * Filtered REQs from Phase 6 - these are already constrained to the
   * current TEXEC jurisdictions. The picker shows them as chips and we
   * pass the user's selection straight to the server.
   */
  filteredRequirements: RelatedRequirement[];
  onClose: () => void;
  onCreated: (bugKey: string) => void;
}

function makeBlankDraft(test: TestReview, gameName: string): BugDraft {
  return {
    summary: `${gameName} - `,
    descriptionMd: [
      '### Expected',
      '(fill in)',
      '',
      '### Actual',
      '(fill in)',
    ].join('\n'),
    severity: 'major',
    stepsToReproduce: (test.test_steps ?? [])
      .map((step) => [step.action, step.data, step.result].filter(Boolean).join(' | '))
      .filter(Boolean),
    environment: '',
    citedRequirementKeys: [],
    suggestedAttachments: [],
  };
}

function refKey(ref: BugAttachmentRef): string {
  return `${ref.source}::${ref.id}`;
}

function formatClock(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

export function BugBuilderModal({
  runId,
  test,
  gameName,
  initialDraft,
  filteredRequirements,
  onClose,
  onCreated,
}: Props) {
  const initial = initialDraft ?? makeBlankDraft(test, gameName);
  const [summary, setSummary] = useState(initial.summary);
  const [description, setDescription] = useState(initial.descriptionMd);
  const [steps, setSteps] = useState<string[]>(
    initial.stepsToReproduce.length ? initial.stepsToReproduce : [''],
  );
  const [environment, setEnvironment] = useState(initial.environment ?? '');
  const [severity, setSeverity] = useState<'minor' | 'major' | 'blocker'>(initial.severity);
  const [previewDescription, setPreviewDescription] = useState(false);
  const [selectedReqKeys, setSelectedReqKeys] = useState<string[]>(() => {
    const allowed = new Set(filteredRequirements.map((r) => r.key));
    return initial.citedRequirementKeys.filter((k) => allowed.has(k));
  });

  const [selectedAttachments, setSelectedAttachments] = useState<BugAttachmentRef[]>(
    () => initial.suggestedAttachments ?? [],
  );

  const [activeTab, setActiveTab] = useState<AttachmentTab>('game-rules');

  const [projects, setProjects] = useState<JiraProject[]>([]);
  const [projectKey, setProjectKey] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [loadingProjects, setLoadingProjects] = useState(true);

  const [gameRulesImages, setGameRulesImages] = useState<RunImage[]>([]);
  const [videos, setVideos] = useState<VideoFrameOption[]>([]);
  const [expandedVideo, setExpandedVideo] = useState<string | null>(null);
  const [chatEvidence, setChatEvidence] = useState<ChatEvidenceItem[]>([]);
  const [stagedUploads, setStagedUploads] = useState<StagedUpload[]>([]);
  const [uploadingLocal, setUploadingLocal] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [linkToTexec, setLinkToTexec] = useState(true);

  const uploadInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api
      .get<JiraProject[]>('/api/projects')
      .then((data) => {
        setProjects(data);
        if (data.length && !projectKey) {
          // Prefer a `BUG` or studio-specific project if obvious, otherwise
          // first in the list. Heuristic only - the user can override.
          const preferred = data.find((p) => /bug/i.test(p.key) || /bug/i.test(p.name));
          setProjectKey((preferred ?? data[0]).key);
        }
      })
      .catch((err) => setError((err as Error).message))
      .finally(() => setLoadingProjects(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    runAssetsApi
      .listImages(runId)
      .then((res) => setGameRulesImages(res.items ?? []))
      .catch(() => setGameRulesImages([]));
    runVideosApi
      .list(runId)
      .then((res) => {
        setVideos(
          (res.items ?? []).map((v) => ({
            videoSlug: v.slug,
            videoFile: v.videoFile,
            durationSeconds: v.durationSeconds,
            frameCount: v.frameCount,
          })),
        );
      })
      .catch(() => setVideos([]));
    api
      .get<ChatEvidenceItem[]>(`/api/runs/${runId}/tests/${test.test_key}/chat/evidence`)
      .then((items) => setChatEvidence(items ?? []))
      .catch(() => setChatEvidence([]));
  }, [runId, test.test_key]);

  const filteredProjects = useMemo(() => {
    const f = projectFilter.trim().toLowerCase();
    if (!f) return projects.slice(0, 50);
    return projects
      .filter((p) => p.key.toLowerCase().includes(f) || p.name.toLowerCase().includes(f))
      .slice(0, 50);
  }, [projects, projectFilter]);

  const selectedKeySet = useMemo(
    () => new Set(selectedAttachments.map(refKey)),
    [selectedAttachments],
  );

  const isSelected = (ref: BugAttachmentRef) => selectedKeySet.has(refKey(ref));

  const toggleAttachment = (ref: BugAttachmentRef) => {
    setSelectedAttachments((prev) => {
      const key = refKey(ref);
      const exists = prev.some((r) => refKey(r) === key);
      if (exists) return prev.filter((r) => refKey(r) !== key);
      return [...prev, ref];
    });
  };

  const removeAttachment = (ref: BugAttachmentRef) => {
    const key = refKey(ref);
    setSelectedAttachments((prev) => prev.filter((r) => refKey(r) !== key));
    if (ref.source === 'upload') {
      api
        .delete(`/api/runs/${runId}/tests/${test.test_key}/bug/uploads/${ref.id}`)
        .catch(() => {});
      setStagedUploads((prev) => prev.filter((u) => u.id !== ref.id));
    }
  };

  const toggleReq = (key: string) => {
    setSelectedReqKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const updateStep = (idx: number, value: string) => {
    setSteps((prev) => prev.map((s, i) => (i === idx ? value : s)));
  };
  const insertStep = (afterIdx: number) => {
    setSteps((prev) => {
      const next = [...prev];
      next.splice(afterIdx + 1, 0, '');
      return next;
    });
  };
  const removeStep = (idx: number) => {
    setSteps((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx)));
  };
  const moveStep = (idx: number, dir: -1 | 1) => {
    setSteps((prev) => {
      const target = idx + dir;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const handleLocalUpload = async (files: FileList | File[]) => {
    const list = Array.from(files);
    if (!list.length) return;
    setUploadingLocal(true);
    setError(null);
    try {
      const fd = new FormData();
      for (const f of list) fd.append('files', f);
      const res = await fetch(
        `/api/runs/${runId}/tests/${test.test_key}/bug/uploads`,
        { method: 'POST', body: fd },
      );
      const json = (await res.json().catch(() => ({}))) as {
        success?: boolean;
        data?: StagedUpload[];
        error?: string;
      };
      if (!res.ok || !json.success || !json.data) {
        throw new Error(json.error || `Upload failed (${res.status})`);
      }
      setStagedUploads((prev) => [...prev, ...(json.data as StagedUpload[])]);
      // Auto-select freshly staged uploads so the user doesn't have to
      // tick each one - they explicitly browsed them.
      setSelectedAttachments((prev) => [
        ...prev,
        ...(json.data as StagedUpload[]).map<BugAttachmentRef>((item) => ({
          source: 'upload',
          id: item.id,
          label: item.filename,
          mime: item.mime,
        })),
      ]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setUploadingLocal(false);
    }
  };

  const submit = async () => {
    if (!projectKey) {
      setError('Pick a project');
      return;
    }
    setSubmitting(true);
    setError(null);
    setWarning(null);
    try {
      const cleanSteps = steps.map((s) => s.trim()).filter(Boolean);
      const data = await api.post<{
        key: string;
        url: string;
        attachments: Array<{ uploaded: boolean; ref: BugAttachmentRef; error?: string }>;
      }>(`/api/runs/${runId}/tests/${test.test_key}/bug`, {
        projectKey,
        summary: summary.trim(),
        descriptionMd: description,
        severity,
        stepsToReproduce: cleanSteps,
        environment: environment.trim(),
        requirementKeys: selectedReqKeys,
        selectedAttachments,
        linkToTexec,
      });
      const failed = data.attachments.filter((a) => !a.uploaded);
      if (failed.length > 0) {
        setWarning(
          `${data.attachments.length - failed.length}/${data.attachments.length} attachments uploaded. Failures: ${failed
            .map((f) => `${f.ref.label || f.ref.id}: ${f.error || 'unknown'}`)
            .join('; ')}`,
        );
      }
      onCreated(data.key);
      // Hold the modal open for a brief beat so the user can see the
      // warning when there is one; otherwise close right away.
      if (failed.length === 0) onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: 1100, width: '92vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="row" style={{ marginBottom: 8 }}>
          <h2 style={{ margin: 0 }}>Bug builder</h2>
          <span className="muted small">
            for test <span className="mono">{test.test_key}</span>
          </span>
          <span className="spacer" />
          <label className="row small" style={{ gap: 4 }}>
            Severity
            <select
              value={severity}
              onChange={(e) => setSeverity(e.target.value as typeof severity)}
            >
              {SEVERITIES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <button className="btn btn-ghost" onClick={onClose}>
            Close
          </button>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) minmax(280px, 360px)',
            gap: 16,
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
          }}
        >
          {/* ---------------- Left column: form ---------------- */}
          <div className="flex-col" style={{ gap: 12, minWidth: 0 }}>
            <div className="flex-col" style={{ gap: 4 }}>
              <label className="small muted">Project</label>
              <input
                placeholder="Filter projects (key or name)..."
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
                disabled={loadingProjects}
              />
              <select
                value={projectKey}
                onChange={(e) => setProjectKey(e.target.value)}
                disabled={loadingProjects}
              >
                {loadingProjects && <option value="">Loading projects...</option>}
                {filteredProjects.map((p) => (
                  <option key={p.key} value={p.key}>
                    [{p.key}] {p.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-col" style={{ gap: 4 }}>
              <label className="small muted">
                Summary <span className="muted">({summary.length} chars)</span>
              </label>
              <input value={summary} onChange={(e) => setSummary(e.target.value)} />
              <p className="muted small" style={{ marginTop: 0 }}>
                Convention: <code>{gameName} - short description</code>. No TLIB / REQ keys.
              </p>
            </div>

            <div className="flex-col" style={{ gap: 4 }}>
              <div className="row">
                <label className="small muted" style={{ flex: 1 }}>
                  Description (Markdown)
                </label>
                <button
                  type="button"
                  className="btn btn-ghost small"
                  onClick={() => setPreviewDescription((p) => !p)}
                >
                  {previewDescription ? 'Edit' : 'Preview'}
                </button>
              </div>
              {previewDescription ? (
                <div className="ai-card" style={{ minHeight: 160 }}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{description}</ReactMarkdown>
                </div>
              ) : (
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  style={{ minHeight: 180 }}
                />
              )}
            </div>

            <div className="flex-col" style={{ gap: 4 }}>
              <label className="small muted">Reproduction Steps (numbered)</label>
              <ol style={{ paddingLeft: 20, margin: 0 }}>
                {steps.map((step, idx) => (
                  <li key={idx} style={{ marginBottom: 4 }}>
                    <div className="row" style={{ gap: 4 }}>
                      <input
                        value={step}
                        placeholder={`Step ${idx + 1}`}
                        onChange={(e) => updateStep(idx, e.target.value)}
                        style={{ flex: 1 }}
                      />
                      <button
                        type="button"
                        className="btn btn-ghost small"
                        title="Move up"
                        onClick={() => moveStep(idx, -1)}
                        disabled={idx === 0}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost small"
                        title="Move down"
                        onClick={() => moveStep(idx, 1)}
                        disabled={idx === steps.length - 1}
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost small"
                        title="Remove this step"
                        onClick={() => removeStep(idx)}
                        disabled={steps.length <= 1}
                      >
                        ×
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost small"
                        title="Insert step below"
                        onClick={() => insertStep(idx)}
                      >
                        +
                      </button>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div className="flex-col" style={{ gap: 4 }}>
              <label className="small muted">Environment</label>
              <input
                value={environment}
                onChange={(e) => setEnvironment(e.target.value)}
                placeholder="Game URL or build version (optional)"
              />
              {initialDraft?.environment && (
                <p className="muted small" style={{ margin: 0 }}>
                  AI suggested: <code>{initialDraft.environment}</code>
                </p>
              )}
            </div>

            <div className="flex-col" style={{ gap: 4 }}>
              <label className="small muted">
                Requirements ({selectedReqKeys.length} of {filteredRequirements.length} selected)
              </label>
              {filteredRequirements.length === 0 ? (
                <p className="muted small" style={{ margin: 0 }}>
                  No REQ keys match the current TEXEC jurisdictions.
                </p>
              ) : (
                <div className="row" style={{ flexWrap: 'wrap', gap: 4 }}>
                  {filteredRequirements.map((req) => {
                    const active = selectedReqKeys.includes(req.key);
                    return (
                      <button
                        key={req.key}
                        type="button"
                        className={`badge mono small ${active ? '' : 'btn-ghost'}`}
                        onClick={() => toggleReq(req.key)}
                        title={`${req.summary || ''} ${
                          req.jurisdictions?.length ? `[${req.jurisdictions.join(', ')}]` : ''
                        }`}
                        style={{
                          cursor: 'pointer',
                          background: active ? 'var(--accent)' : undefined,
                          color: active ? '#fff' : undefined,
                          borderColor: active ? 'var(--accent)' : undefined,
                        }}
                      >
                        {active ? '✓ ' : ''}{req.key}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <AttachmentPicker
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              gameRulesImages={gameRulesImages}
              videos={videos}
              expandedVideo={expandedVideo}
              setExpandedVideo={setExpandedVideo}
              chatEvidence={chatEvidence}
              stagedUploads={stagedUploads}
              runId={runId}
              testKey={test.test_key}
              isSelected={isSelected}
              toggleAttachment={toggleAttachment}
              uploadingLocal={uploadingLocal}
              onPickLocalFiles={() => uploadInputRef.current?.click()}
            />

            <input
              ref={uploadInputRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => {
                if (e.target.files) void handleLocalUpload(e.target.files);
                e.target.value = '';
              }}
            />

            <label className="row small">
              <input
                type="checkbox"
                checked={linkToTexec}
                onChange={(e) => setLinkToTexec(e.target.checked)}
              />
              Link to TEXEC ("created by")
            </label>
          </div>

          {/* ---------------- Right column: selected sidebar ---------------- */}
          <div
            className="flex-col"
            style={{
              gap: 8,
              padding: 12,
              background: 'var(--bg-elev)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              minWidth: 0,
              alignSelf: 'flex-start',
            }}
          >
            <strong>Selected attachments ({selectedAttachments.length})</strong>
            {selectedAttachments.length === 0 ? (
              <p className="muted small" style={{ margin: 0 }}>
                Pick screenshots, frames, chat evidence, or upload files from the tabs on the left.
              </p>
            ) : (
              <div className="flex-col" style={{ gap: 4 }}>
                {selectedAttachments.map((ref) => (
                  <div
                    key={refKey(ref)}
                    className="row"
                    style={{
                      gap: 6,
                      padding: '4px 6px',
                      borderRadius: 4,
                      background: 'var(--bg)',
                      border: '1px solid var(--border)',
                    }}
                  >
                    <span className="badge small" title={ref.source}>
                      {ref.source.replace(/-/g, ' ')}
                    </span>
                    <span
                      className="mono small"
                      style={{
                        flex: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {ref.label || ref.id}
                    </span>
                    <button
                      type="button"
                      className="btn btn-ghost small"
                      onClick={() => removeAttachment(ref)}
                      title="Remove"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            <hr style={{ width: '100%', border: 'none', borderTop: '1px solid var(--border)' }} />
            <p className="muted small" style={{ margin: 0 }}>
              Reporter: your Jira user (resolved from your API token).
              <br />
              Assignee: blank by default.
            </p>
          </div>
        </div>

        {error && (
          <div
            role="alert"
            style={{
              marginTop: 8,
              padding: '8px 12px',
              border: '1px solid var(--bad)',
              background: 'rgba(248, 81, 73, 0.08)',
              borderRadius: 6,
              color: 'var(--bad)',
            }}
          >
            {error}
          </div>
        )}
        {warning && (
          <div
            role="status"
            style={{
              marginTop: 8,
              padding: '8px 12px',
              border: '1px solid var(--warning, #b58900)',
              background: 'rgba(255, 196, 0, 0.08)',
              borderRadius: 6,
            }}
          >
            {warning}
          </div>
        )}

        <div className="row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
          <button className="btn" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={() => void submit()}
            disabled={submitting || !projectKey || !summary.trim()}
          >
            {submitting ? 'Creating bug...' : 'Create bug'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Attachment picker
// ---------------------------------------------------------------------------

interface PickerProps {
  activeTab: AttachmentTab;
  setActiveTab: (tab: AttachmentTab) => void;
  gameRulesImages: RunImage[];
  videos: VideoFrameOption[];
  expandedVideo: string | null;
  setExpandedVideo: (slug: string | null) => void;
  chatEvidence: ChatEvidenceItem[];
  stagedUploads: StagedUpload[];
  runId: string;
  testKey: string;
  isSelected: (ref: BugAttachmentRef) => boolean;
  toggleAttachment: (ref: BugAttachmentRef) => void;
  uploadingLocal: boolean;
  onPickLocalFiles: () => void;
}

function AttachmentPicker(props: PickerProps) {
  const {
    activeTab,
    setActiveTab,
    gameRulesImages,
    videos,
    expandedVideo,
    setExpandedVideo,
    chatEvidence,
    stagedUploads,
    runId,
    isSelected,
    toggleAttachment,
    uploadingLocal,
    onPickLocalFiles,
  } = props;

  return (
    <div className="flex-col" style={{ gap: 6 }}>
      <label className="small muted">Attachments</label>
      <div className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
        {ATTACHMENT_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className="btn small"
            onClick={() => setActiveTab(tab.id)}
            style={{
              background: activeTab === tab.id ? 'var(--accent)' : undefined,
              color: activeTab === tab.id ? '#fff' : undefined,
              borderColor: activeTab === tab.id ? 'var(--accent)' : undefined,
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div
        style={{
          padding: 8,
          border: '1px solid var(--border)',
          borderRadius: 6,
          minHeight: 120,
          maxHeight: 320,
          overflow: 'auto',
        }}
      >
        {activeTab === 'game-rules' && (
          <GameRulesTab
            runId={runId}
            images={gameRulesImages}
            isSelected={isSelected}
            toggle={toggleAttachment}
          />
        )}
        {activeTab === 'video-frames' && (
          <VideoFramesTab
            runId={runId}
            videos={videos}
            expandedVideo={expandedVideo}
            setExpandedVideo={setExpandedVideo}
            isSelected={isSelected}
            toggle={toggleAttachment}
          />
        )}
        {activeTab === 'chat-evidence' && (
          <ChatEvidenceTab
            items={chatEvidence}
            isSelected={isSelected}
            toggle={toggleAttachment}
          />
        )}
        {activeTab === 'upload' && (
          <UploadTab
            stagedUploads={stagedUploads}
            uploading={uploadingLocal}
            onPickFiles={onPickLocalFiles}
            isSelected={isSelected}
            toggle={toggleAttachment}
          />
        )}
      </div>
    </div>
  );
}

function GameRulesTab({
  runId,
  images,
  isSelected,
  toggle,
}: {
  runId: string;
  images: RunImage[];
  isSelected: (ref: BugAttachmentRef) => boolean;
  toggle: (ref: BugAttachmentRef) => void;
}) {
  if (images.length === 0) {
    return <p className="muted small">No game-rules screenshots are stored on this run.</p>;
  }
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
        gap: 8,
      }}
    >
      {images.map((img) => {
        const ref: BugAttachmentRef = {
          source: 'game-rules-image',
          id: img.name,
          label: img.name,
          mime: 'image/png',
        };
        const active = isSelected(ref);
        return (
          <button
            key={img.name}
            type="button"
            onClick={() => toggle(ref)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              padding: 4,
              background: active ? 'var(--accent)' : 'var(--bg-elev)',
              color: active ? '#fff' : 'var(--text)',
              border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
              borderRadius: 4,
              cursor: 'pointer',
              minWidth: 0,
            }}
            title={img.name}
          >
            <img
              src={runAssetsApi.imageUrl(runId, img.name)}
              alt={img.name}
              loading="lazy"
              style={{ width: '100%', aspectRatio: '16 / 9', objectFit: 'cover', borderRadius: 3 }}
            />
            <code
              className="mono small"
              style={{
                color: active ? '#fff' : 'var(--text-dim)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {img.name}
            </code>
          </button>
        );
      })}
    </div>
  );
}

function VideoFramesTab({
  runId,
  videos,
  expandedVideo,
  setExpandedVideo,
  isSelected,
  toggle,
}: {
  runId: string;
  videos: VideoFrameOption[];
  expandedVideo: string | null;
  setExpandedVideo: (slug: string | null) => void;
  isSelected: (ref: BugAttachmentRef) => boolean;
  toggle: (ref: BugAttachmentRef) => void;
}) {
  if (videos.length === 0) {
    return <p className="muted small">No gameplay videos uploaded for this run.</p>;
  }
  const MAX_FRAMES_RENDERED = 60;
  return (
    <div className="flex-col" style={{ gap: 8 }}>
      {videos.map((video) => {
        const open = expandedVideo === video.videoSlug;
        const total = video.frameCount > 0 ? video.frameCount : (video.durationSeconds ?? 0);
        const sampled = total > MAX_FRAMES_RENDERED;
        const step = sampled ? Math.ceil(total / MAX_FRAMES_RENDERED) : 1;
        const seconds: number[] = [];
        for (let i = 0; i < total; i += step) seconds.push(i);
        return (
          <div
            key={video.videoSlug}
            style={{
              border: '1px solid var(--border)',
              borderRadius: 4,
              padding: 6,
              background: 'var(--bg-elev)',
            }}
          >
            <div className="row" style={{ gap: 6 }}>
              <code className="mono small" style={{ flex: 1, minWidth: 0 }}>
                {video.videoSlug}
              </code>
              <span className="muted small">
                {video.frameCount} frame{video.frameCount === 1 ? '' : 's'}
              </span>
              <button
                type="button"
                className="btn btn-ghost small"
                onClick={() => setExpandedVideo(open ? null : video.videoSlug)}
              >
                {open ? 'Hide' : 'Show frames'}
              </button>
            </div>
            {open && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                  gap: 6,
                  marginTop: 6,
                }}
              >
                {seconds.map((sec) => {
                  const frameName = `frame_${String(sec).padStart(5, '0')}.jpg`;
                  const url = runVideosApi.frameUrl(runId, video.videoSlug, frameName);
                  const id = `videos/${video.videoSlug}/frames/${frameName}`;
                  const label = `${video.videoSlug} @ ${formatClock(sec)}`;
                  const ref: BugAttachmentRef = {
                    source: 'video-frame',
                    id,
                    label,
                    mime: 'image/jpeg',
                  };
                  const active = isSelected(ref);
                  return (
                    <button
                      key={sec}
                      type="button"
                      onClick={() => toggle(ref)}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                        padding: 4,
                        background: active ? 'var(--accent)' : 'var(--bg)',
                        color: active ? '#fff' : 'var(--text)',
                        border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                        borderRadius: 4,
                        cursor: 'pointer',
                        minWidth: 0,
                      }}
                    >
                      <img
                        src={url}
                        alt={label}
                        loading="lazy"
                        style={{
                          width: '100%',
                          aspectRatio: '16 / 9',
                          objectFit: 'cover',
                          borderRadius: 3,
                        }}
                      />
                      <code
                        className="mono small"
                        style={{ color: active ? '#fff' : 'var(--text-dim)' }}
                      >
                        {formatClock(sec)}
                      </code>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ChatEvidenceTab({
  items,
  isSelected,
  toggle,
}: {
  items: ChatEvidenceItem[];
  isSelected: (ref: BugAttachmentRef) => boolean;
  toggle: (ref: BugAttachmentRef) => void;
}) {
  if (items.length === 0) {
    return (
      <p className="muted small">
        Drop a file in the chat below ("Chat with AI about this test") to make it selectable here.
      </p>
    );
  }
  return (
    <div className="flex-col" style={{ gap: 4 }}>
      {items.map((item) => {
        const ref: BugAttachmentRef = {
          source: 'chat-evidence',
          id: item.id,
          label: item.filename,
          mime: item.mime,
        };
        const active = isSelected(ref);
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => toggle(ref)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 8px',
              background: active ? 'var(--accent)' : 'var(--bg-elev)',
              color: active ? '#fff' : 'var(--text)',
              border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
              borderRadius: 4,
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <span className="badge small">{item.kind}</span>
            <code
              className="mono small"
              style={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {item.filename}
            </code>
            <span className="muted small">{item.mime}</span>
          </button>
        );
      })}
    </div>
  );
}

function UploadTab({
  stagedUploads,
  uploading,
  onPickFiles,
  isSelected,
  toggle,
}: {
  stagedUploads: StagedUpload[];
  uploading: boolean;
  onPickFiles: () => void;
  isSelected: (ref: BugAttachmentRef) => boolean;
  toggle: (ref: BugAttachmentRef) => void;
}) {
  return (
    <div className="flex-col" style={{ gap: 8 }}>
      <div className="row" style={{ gap: 8 }}>
        <button
          type="button"
          className="btn small"
          onClick={onPickFiles}
          disabled={uploading}
        >
          {uploading ? 'Uploading...' : 'Browse files...'}
        </button>
        <span className="muted small">
          Files stage on the server until the bug is filed.
        </span>
      </div>
      {stagedUploads.length === 0 ? (
        <p className="muted small" style={{ margin: 0 }}>
          No staged files yet.
        </p>
      ) : (
        <div className="flex-col" style={{ gap: 4 }}>
          {stagedUploads.map((u) => {
            const ref: BugAttachmentRef = {
              source: 'upload',
              id: u.id,
              label: u.filename,
              mime: u.mime,
            };
            const active = isSelected(ref);
            return (
              <button
                key={u.id}
                type="button"
                onClick={() => toggle(ref)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 8px',
                  background: active ? 'var(--accent)' : 'var(--bg-elev)',
                  color: active ? '#fff' : 'var(--text)',
                  border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                  borderRadius: 4,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <span className="badge small">upload</span>
                <code
                  className="mono small"
                  style={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {u.filename}
                </code>
                <span className="muted small">
                  {(u.size / 1024).toFixed(1)} KB
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
